<div class="modal-header">
  <h4 class="modal-title">
    @if (mode === 'select') {
      Import / Export Templates
    } @else if (mode === 'export') {
      Export Template: {{ selectedTemplateName }}
    } @else {
      Import Template(s)
    }
  </h4>
</div>

<div class="modal-body">
  <!-- Selection Mode -->
  @if (mode === 'select') {
  @if (isLoading) {
  <div class="text-center py-5">
    <div class="spinner-border" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
    <p class="mt-3">Loading templates...</p>
  </div>
  } @else {
  <div class="selection-section">
    <div class="row">
      <div class="col-md-6">
        <div class="card">
          <div class="card-body text-center">
            <i class="fas fa-upload fa-3x mb-3" aria-hidden="true"></i>
            <h5>Export Templates</h5>
            @if (templateOptions.length === 0) {
            <p class="text-muted">No templates available to export. Create a template first, then return here to export it.</p>
            <button class="btn btn-secondary" disabled aria-label="No templates available for export">
              No Templates Available
            </button>
            } @else {
            <p class="text-muted">Download template file(s) to share with other accounts</p>

            <form [formGroup]="shareForm">
              <div class="mb-3">
                <select class="form-select" formControlName="selectedTemplate" aria-label="Select template to export">
                  <option value="">Select template to export</option>
                  @for (template of templateOptions; track template.key) {
                  <option [value]="template.key">{{ template.value }}</option>
                  }
                </select>
              </div>
            </form>

            <div class="d-flex gap-2 justify-content-center flex-wrap">
              <button class="btn btn-primary"
                      (click)="selectExport()"
                      [disabled]="!shareForm.get('selectedTemplate')?.value"
                      aria-label="Export selected template">
                Export Selected
              </button>
              <button class="btn btn-secondary"
                      (click)="exportAllTemplates()"
                      [disabled]="isLoading"
                      aria-label="Export all templates">
                Export All ({{ templateOptions.length }})
              </button>
            </div>
            }
          </div>
        </div>
      </div>

      <div class="col-md-6">
        <div class="card">
          <div class="card-body text-center">
            <i class="fas fa-download fa-3x mb-3" aria-hidden="true"></i>
            <h5>Import Templates</h5>
            <p class="text-muted">Upload template file(s) from another account</p>

            <button class="btn btn-primary" (click)="selectImport()" aria-label="Go to import template form">
              Import Templates
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  }
  }

  <!-- Import Mode -->
  @if (mode === 'import') {
  <div class="import-section">
    <form [formGroup]="shareForm">
      <input type="file"
             class="d-none"
             id="templateFileHidden"
             accept=".json"
             multiple
             #fileInput
             (change)="onFileSelected($event)">

      @if (importFiles.length === 0) {
      <div class="mb-3">
        <div>
          <button type="button"
                  class="btn btn-secondary"
                  (click)="fileInput.click()"
                  aria-label="Choose files to import">
            <i class="fas fa-folder-open me-2" aria-hidden="true"></i>Choose Files
          </button>
        </div>
        <div class="form-text mt-2">
          Select .json template file(s) exported from another account.
        </div>
      </div>
      }

      @if (importFiles.length > 0) {
      <div class="mb-3">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <label class="form-label mb-0">Files to import ({{ importFiles.length }}):</label>
          <button type="button"
                  class="btn btn-sm btn-secondary"
                  (click)="fileInput.click()"
                  aria-label="Add more files">
            <i class="fas fa-plus me-1" aria-hidden="true"></i> Add More
          </button>
        </div>
        <div class="list-group">
          @for (file of importFiles; track file.name; let i = $index) {
          <div class="list-group-item">
            <div class="d-flex justify-content-between align-items-start">
              <div class="flex-grow-1 me-2">
                <div class="d-flex align-items-center mb-1">
                  <i class="fas fa-file-code me-2" aria-hidden="true"></i>
                  <small class="text-muted">{{ file.name }}</small>
                </div>
                <div class="d-flex align-items-center">
                  <label class="form-label mb-0 me-2 text-nowrap"><small>Name:</small></label>
                  <input type="text"
                         class="form-control form-control-sm"
                         [class.is-invalid]="isNameDuplicate(importFileNames[file.name], file.name) || !importFileNames[file.name]?.trim()"
                         [value]="importFileNames[file.name]"
                         (input)="updateImportFileName(file.name, $any($event.target).value)"
                         placeholder="Template name"
                         aria-label="Template name for {{ file.name }}">
                </div>
                @if (isNameDuplicate(importFileNames[file.name], file.name)) {
                <small class="text-danger">Duplicate template name</small>
                } @else if (!importFileNames[file.name]?.trim()) {
                <small class="text-danger">Template name is required</small>
                }
              </div>
              <button type="button"
                      class="btn btn-sm btn-outline-danger rounded-circle"
                      style="width: 28px; height: 28px; padding: 0;"
                      (click)="removeImportFile(i)"
                      aria-label="Remove file">
                <i class="fas fa-times" aria-hidden="true"></i>
              </button>
            </div>
          </div>
          }
        </div>
      </div>
      }
    </form>
  </div>
  }
</div>

<div class="modal-footer">
  @if (mode === 'import') {
    <button type="button" class="btn btn-secondary" (click)="backToSelect()" aria-label="Go back to template selection">
      <i class="fas fa-arrow-left" aria-hidden="true"></i> Back
    </button>
  }
  
  <button type="button" class="btn btn-secondary" (click)="onCancel()" aria-label="Cancel and close dialog">
    Cancel
  </button>
  
  @if (mode === 'import') {
    <button 
      type="button" 
      class="btn btn-primary" 
      (click)="importTemplate()"
      [disabled]="!canImport()"
      aria-label="Import selected template file(s)">
      @if (isLoading) {
        <div class="spinner-border spinner-border-sm me-2" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      }
      @if (importFiles.length > 1) {
        Import {{ importFiles.length }} Templates
      } @else {
        Import Template
      }
    </button>
  }
</div>
